import React, { useEffect, useRef, useState } from 'react';
import { CalendarIcon } from '@radix-ui/react-icons';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import dayjs from 'dayjs';
import '../../Styles/Common.css';
import PropTypes from 'prop-types';
import { TimeField } from '../ui/date-time-picker/time-field';

const DateInput = ({ className, iconClassName, iconColor, value, disabled, label, required, onValueChange, name, lableComponent,minDate,maxDate, showTime }) => {
    const [dateTime, setDateTime] = useState(new Date());
    const initialDate = dayjs(new Date()).format('HH:mm')?.split(':');
    const [dateState, setDateState] = useState({ hour: initialDate[0], minute: initialDate[1] });
    const timeFieldRef = useRef(null);

    useEffect(() => {
        onValueChange(dateTime, name);
    }, [dateTime]);

    const handleDateChange = (date) => {
        setDateTime((prevDateTime) => {
            if (showTime) {
                const time = dayjs(prevDateTime).format('HH:mm');
                return new Date(dayjs(date).set('hour', time.split(':')[0]).set('minute', time.split(':')[1]));
            } else {
                return new Date(dayjs(date));
            }
        });

        if (showTime && timeFieldRef.current) {
            timeFieldRef.current.focus();
        }
    };

    const handleTimeChange = (time) => {
        setDateState(time);
        updateDateTime(time);
    };

    const updateDateTime = (time) => {
        const { hour, minute } = time;
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');

        const newDateTime = new Date(dateTime);
        newDateTime.setHours(parseInt(formattedHour));
        newDateTime.setMinutes(parseInt(formattedMinute));

        setDateTime(newDateTime);
    };

    const handleCalendarSelect = () => {
        if (showTime && timeFieldRef.current) {
            timeFieldRef.current.focus();
        }
    };

    return (
        <div>
            {label && (
                <label className={cn('flex w-full flex-row')}>
                    <span className={cn('grc-label block mb-1 flex flex-row')}>{label}</span>
                    {required && <span className="text-danger">*</span>}
                    <span className="">{lableComponent}</span>
                </label>
            )}
            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        variant={'dateInput'}
                        disabled={disabled}
                        className={cn(
                            'w-full justify-start mt-[5px] text-left font-normal',
                            !value || new Date() && 'text-muted-foreground',
                            'date-pick-btn',
                            className
                        )}
                        style={{ color: dayjs(value || new Date()).format('DD-MM-YYYY') ? 'hsl(var(--muted))' : 'hsl(var(--grey))' }}
                    >
                        {value ? dayjs(value || new Date()).format(showTime ? 'DD-MM-YYYY HH:mm' : 'DD-MM-YYYY') || <span>Pick a date{showTime && ' and time'}</span> : ''}
                        <CalendarIcon color={iconColor} className={cn(iconClassName,"ml-auto h-4 w-4")} />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    align="end"
                    className="flex w-auto flex-col space-y-2 p-2"
                >
                    <Calendar
                        defaultMonth={new Date(value || new Date())}
                        defaultDate={new Date(value || new Date())}
                        mode="single"
                        selected={typeof value === 'string' ? new Date(value) : value || new Date()}
                        initialFocus
                        onSelect={handleDateChange}
                        onDaySelect={handleCalendarSelect}
                        captionLayout="dropdown-buttons"
                        fromYear={1900}
                        toYear={new Date().getFullYear()}
                        minDate={minDate}
                        maxDate={maxDate}
                    />
                    {showTime && (
                        <TimeField childRef={timeFieldRef} value={dateState} onChange={handleTimeChange} />
                    )}
                </PopoverContent>
            </Popover>
        </div>
    );
};

DateInput.propTypes = {
    label: PropTypes.string,
    required: PropTypes.bool,
    name: PropTypes.string,
    onValueChange: PropTypes.func,
    showTime: PropTypes.bool, // Add this prop to propTypes
};

export default DateInput;
